import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Save, Send, Mail } from "lucide-react";
import { toast } from "sonner";

const PLACEHOLDERS = ["customer_name", "customer_email", "order_id", "status", "placed_at", "total", "subtotal", "discount", "shipping_cost", "invoice_number", "tracking_number", "items_html", "due_date"];

export default function AdminEmailTemplates() {
  const [tpls, setTpls] = useState([]);
  const [selected, setSelected] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [testTo, setTestTo] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const load = () => api.get("/admin/email-templates").then((r) => {
    setTpls(r.data);
    if (!selected && r.data[0]) pick(r.data[0]);
  });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const pick = (t) => {
    setSelected(t.template_key);
    setSubject(t.subject || "");
    setBody(t.html_body || "");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/email-templates/${selected}`, { template_key: selected, subject, html_body: body });
      toast.success("Template saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  const test = async () => {
    if (!testTo) { toast.error("Enter a test recipient"); return; }
    setSending(true);
    try {
      const r = await api.post(`/admin/email-templates/${selected}/test`, { to: testTo });
      if (r.data.result?.sent) toast.success(`Test email sent to ${testTo}`);
      else toast.warning(`Fallback logged (not sent): ${r.data.result?.reason || "Resend sandbox"}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); } finally { setSending(false); }
  };

  return (
    <div data-testid="admin-email-templates-page">
      <div className="mb-6">
        <div className="eyebrow mb-2">Email Templates</div>
        <h1 className="font-display text-3xl font-semibold text-slate-900 tracking-tight">Transactional email templates</h1>
        <p className="mt-1 text-sm text-slate-600">Rendered via a lightweight <code className="bg-slate-100 px-1 rounded text-xs">{"{{placeholder}}"}</code> engine. See supported placeholders below.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
        <aside className="border border-slate-200 rounded-md bg-white overflow-hidden">
          {tpls.map((t) => (
            <button
              key={t.template_key}
              onClick={() => pick(t)}
              data-testid={`tpl-${t.template_key}`}
              className={`w-full text-left px-4 py-3 border-b border-slate-100 last:border-b-0 flex items-start gap-2 hover:bg-slate-50 ${selected === t.template_key ? "bg-blue-50/40 border-l-2 border-l-blue-500" : ""}`}
            >
              <Mail className="w-4 h-4 text-slate-400 mt-0.5" strokeWidth={1.5}/>
              <div className="min-w-0">
                <div className="text-sm text-slate-900 truncate">{t.template_key}</div>
                <div className="text-xs text-slate-500 truncate">{t.subject}</div>
              </div>
            </button>
          ))}
        </aside>

        <div className="space-y-4">
          <div className="border border-slate-200 rounded-md bg-white p-5 space-y-3">
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Subject</label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="tpl-subject"/>
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-slate-500">HTML body</label>
              <Textarea rows={16} value={body} onChange={(e) => setBody(e.target.value)} data-testid="tpl-body" className="font-mono text-xs"/>
            </div>
            <div className="text-xs text-slate-500">
              <span className="font-mono uppercase tracking-widest text-[10px] text-slate-400 mr-2">Placeholders:</span>
              {PLACEHOLDERS.map((p) => (
                <button key={p} type="button" onClick={() => setBody((b) => b + `{{${p}}}`)} className="mr-1 mb-1 px-2 py-0.5 border border-slate-200 rounded text-[11px] font-mono hover:bg-slate-100">{`{{${p}}}`}</button>
              ))}
            </div>
            <div className="flex items-center gap-2 pt-2">
              <Button data-testid="tpl-save" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white"><Save className="w-4 h-4 mr-1"/> Save</Button>
              <div className="flex-1"/>
              <Input placeholder="Send test to…" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="tpl-test-email" className="w-64"/>
              <Button variant="outline" data-testid="tpl-test-btn" onClick={test} disabled={sending} className="border-slate-300"><Send className="w-4 h-4 mr-1"/> Send test</Button>
            </div>
          </div>

          <div className="border border-slate-200 rounded-md bg-white p-5">
            <div className="font-display text-sm font-semibold mb-2">Preview</div>
            <iframe title="Preview" srcDoc={body.replace(/\{\{(\w+)\}\}/g, (_, k) => `<span style='background:#fde68a;padding:0 3px'>${k}</span>`)} className="w-full h-72 border border-slate-200 rounded"/>
          </div>
        </div>
      </div>
    </div>
  );
}
